/* eslint-disable no-restricted-syntax */
import {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';

import {
  AdminRoles,
  ClientRoles,
  EmployeeRoles,
  type IFrontendUrls,
  INIT_PAGE,
  type IPaginationMetaProps,
  type IPaginationResponseData,
  type IResponseDefaultContent,
  type IRole,
  type IThrowsError,
  Logger,
  ManagerRoles,
  NO_LIMIT_PAGE,
  Roles,
  removeSessionStorage,
} from '@gbs-monorepo-packages/common';

import { AuthStorageKey } from '../constants/Storage';
import { generateUserUrlSearchParams } from '../utils';

export interface IAuthUrls {
  profileUpdate: string;
  refresh: string;
  resetPassword: string;
  signIn: string;
  signOut: string;
  unregister: string;
}
export interface IResponseWithCode {
  code: number;
  message: string;
}

export interface IResponseAccountDTO extends IResponseDefaultContent {
  data: IUserDTO;
  token: string;
}

export type IResponseAccountWithCodeDTO = IResponseAccountDTO &
  IResponseWithCode;

export interface IForgotCredentialsDTO {
  email: string;
}

export interface IRequestAccessDTO {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

export interface IResetPasswordCredentialsDTO {
  email: string;
  password: string;
}

export interface ISignInCredentialsDTO {
  email: string;
  password: string;
}

export interface IUnregisterCredentialsDTO {
  id: number;
}

export interface ISignOutCredentialsDTO {
  email: string;
}

export interface IClientCompanyDTO {
  id: number;
  clientId: number;
  firstName: string;
  lastName: string;
}

export interface IAccountFile {
  cloudStorageKey: string;
  path: string;
  extension: string;
  createdAt: string;
}

export interface IGetManagersProps {
  page: number;
  limit: number;
  filter: string;
  all?: boolean;
}

export interface IPaginationManagersDTO {
  data: IUserDTO[];
  meta: IPaginationMetaProps;
}

export interface IGetUsersByRolesProps {
  roles: string;
  page: number;
  limit: number;
  filter: string;
  all?: boolean;
  clientId: number | null;
  isClientAdmin?: string[];
}

export interface IPaginationUsersByRolesDTO {
  data: IUserDTO[];
  meta: IPaginationMetaProps;
}

export interface ISettingsDTO {
  setting: number;
  settingValue: string;
}

export interface IUserClientDTO {
  id: number;
  clientName: string;
  clientCompanyFile: {
    cloudStorageKey: string;
    createdAt: string;
    extension: string;
    path: string;
  } | null;
  isAgency: boolean | null;
}

export interface IUserDTO {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  permissions?: string[]; // not implemented yet
  roles: string[];
  status?: string;
  clientId?: number;
  clientCompany?: IUserClientDTO;
  currentRole?: IRole | null;
  phone?: string;
  accountFile?: IAccountFile | null;
  settings?: ISettingsDTO[];
  agencies?: IUserClientDTO[];
  isClientAdmin?: string[];
  currentTermsVersionAccepted?: number;
  isMainManager: string[];
}

export interface IUserAgencyDTO extends Omit<IUserDTO, 'agencies'> {
  agencies: {
    id: number;
    clientName: string;
  }[];
}

export type IInviteUserDTO = Omit<IUserDTO, 'id' | 'permissions' | 'status'>;

const edgeRoute: string =
  import.meta.env.VITE_EDGE_URL ?? 'http://localhost:5173';

const adminRoute: string =
  import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:5174';

const clientRoute: string =
  import.meta.env.VITE_CLIENT_URL ?? 'http://localhost:5175';

const employeeRoute: string =
  import.meta.env.VITE_EMPLOYEE_URL ?? 'http://localhost:5176';

export class AuthService {
  private readonly api: AxiosInstance;
  private readonly url = {
    accessRequest: 'access-request',
    forgotPassword: 'reset-password',
    resetPassword: 'reset-password/change-password',
    refresh: 'api/refresh',
    signIn: 'api/login',
    signOut: 'api/logout',
    unregister: 'delete-account',
    getManagers: 'api/admin-user/managers',
  };

  private readonly frontendUrl: IFrontendUrls = {
    edgeRoute,
    adminRoute,
    clientRoute,
    employeeRoute,
  };

  constructor(
    axiosInstance: AxiosInstance,
    url?: Partial<IAuthUrls>,
    frontendUrl?: Partial<IFrontendUrls>
  ) {
    this.api = axiosInstance;
    if (url) {
      this.url = { ...this.url, ...url };
    }
    if (frontendUrl) {
      this.frontendUrl = { ...this.frontendUrl, ...frontendUrl };
    }
  }

  public async forgotPassword({ email }: IForgotCredentialsDTO): Promise<void> {
    const response = await this.api
      .post<IResponseDefaultContent>(this.url.forgotPassword, { email })
      .then((resp) => resp.data)
      .catch((err: IThrowsError) => {
        // throw err;
        Logger.debug('auth err:', err);
      });

    Logger.debug('response: ', response);
  }

  public async requestAccess({
    firstName,
    lastName,
    email,
    company,
  }: IRequestAccessDTO): Promise<void> {
    await this.api
      .post<IResponseDefaultContent>(this.url.accessRequest, {
        firstName,
        lastName,
        email,
        company,
      })
      .then((resp) => resp.data);
  }

  public async resetPassword({
    email,
    password,
  }: IResetPasswordCredentialsDTO): Promise<IUserDTO> {
    const { data } = await this.api
      .patch<IResponseAccountDTO>(this.url.resetPassword, {
        email,
        password,
      })
      .then((resp) => resp.data)
      .catch((err: IThrowsError) => {
        throw err;
      });

    return data;
  }

  public async refresh(): Promise<IUserDTO> {
    const payload = await this.api
      .get<IResponseAccountWithCodeDTO>(this.url.refresh)
      .then((resp) => {
        localStorage.setItem('JWT_TOKEN', resp.data.token);
        return resp.data;
      })
      .catch((err: AxiosError<IResponseWithCode>) => {
        throw err;
      });

    Logger.debug('response: ', payload);
    if (payload.data === undefined || payload.data === null) {
      throw new Error('Refresh response missing user data');
    }
    return payload.data;
  }

  public async signIn({
    email,
    password,
  }: ISignInCredentialsDTO): Promise<IResponseAccountWithCodeDTO> {
    const data = await this.api
      .post<IResponseAccountWithCodeDTO>(this.url.signIn, { email, password })
      .then((resp) => resp.data)
      .catch((err: AxiosError<IResponseWithCode>) => {
        throw err;
      });

    return data;
  }

  public getUserMainPortal({ roles }: IUserDTO): string {
    switch (true) {
      case roles.some((role) => AdminRoles.includes(role as Roles)):
      case roles.some((role) => ManagerRoles.includes(role as Roles)):
        return this.frontendUrl.adminRoute;
      case roles.some((role) => ClientRoles.includes(role as Roles)):
        return this.frontendUrl.clientRoute;
      case roles.some((role) => EmployeeRoles.includes(role as Roles)):
        return this.frontendUrl.employeeRoute;
      default:
        return this.frontendUrl.edgeRoute;
    }
  }

  public getRedirectPortal(usrData: IUserDTO, token: string): string {
    const currentPortal = window.location.origin;
    let redirectPortal = this.getUserMainPortal(usrData);

    if (currentPortal === redirectPortal) {
      return '';
    }

    const userInfoUrlSearchParams = generateUserUrlSearchParams(usrData, token);

    redirectPortal += `/permission-redirect?${userInfoUrlSearchParams.toString()}`;

    return redirectPortal;
  }

  public async signOut(): Promise<void> {
    const response = await this.api
      .get<string>(this.url.signOut)
      .then((resp) => {
        localStorage.removeItem('JWT_TOKEN');
        removeSessionStorage(AuthStorageKey.IS_ADMIN_VIEWING_AS_CLIENT);
        return resp.data;
      })
      .catch((err: AxiosError<string>) => {
        throw err;
      });

    Logger.debug('response: ', response);
  }

  public async unregister({ id }: IUnregisterCredentialsDTO): Promise<void> {
    const response = await this.api
      .delete<IResponseDefaultContent>(`${this.url.unregister}?id=${id}`)
      .then((resp) => resp.data)
      .catch((err: IThrowsError) => {
        // throw err;
        Logger.debug('auth err:', err);
      });

    Logger.debug('response: ', response);
  }

  public async getManagers(
    { page, limit, filter, all = false }: IGetManagersProps,
    config?: AxiosRequestConfig
  ): Promise<IPaginationManagersDTO> {
    const result = await this.api
      .get<IPaginationResponseData<IUserDTO[]>>(
        `${this.url.getManagers}?page=${all ? INIT_PAGE : page}&limit=${
          all ? NO_LIMIT_PAGE : limit
        }&filter=${all ? '' : encodeURIComponent(filter)}`,
        config
      )
      .then((resp) => resp.data)
      .catch((err: IThrowsError) => {
        throw err;
      });

    return result;
  }

  getEdgeRoute(): string {
    return this.frontendUrl.edgeRoute;
  }

  getAdminRoute(): string {
    return this.frontendUrl.adminRoute;
  }

  getClientRoute(): string {
    return this.frontendUrl.clientRoute;
  }
}
